// ═══════════════════════════════════════════════════════════════════════════
//  OpenPositionsPanel — live open positions, top of the right-hand column.
//
//  Lifted out of LiveTrading.tsx (God 2026-08-26, phase 3). Upgraded for God's
//  Phase 3 order (2026-09-24) so every open row of the running bots shows:
//    · the ASSET — underlying named beside the strike + CE/PE + expiry
//    · the LIVE LTP of the contract itself (option premium / futures / stock),
//      with its source (ws · chain · bridge), never the index spot
//    · AVG BUY / AVG SELL (the entry side), SL in PREMIUM when the ledger stamped
//      `dnaSlPremium`, otherwise the UNDERLYING stop/target — labelled as such
//    · Running M2M = (live − entry) × openQty × direction (pnlMath.runningM2M)
//    · a CHART action that opens the contract at the row's own timeframe
//  and the header totals Booked (session realised) · M2M · Total.
//
//  All arithmetic is in ./pnlMath — this file formats, it does not compute.
//  The bridge's own figure (`openPnl`, /bridge/rag/positions) is kept as the
//  price of last resort and shown in the M2M tooltip as a cross-check.
//
//  Rendered by both lanes — DUMABATI and KALI share the page component.
// ═══════════════════════════════════════════════════════════════════════════
import React from 'react';
import { Activity } from 'lucide-react';
import { fmtM, fmtTime } from './foundation';
import { contractParts, levelsOf, runningM2M, sumM2M, totalPnl } from './pnlMath';
import type { LiveLtp } from './useContractLtps';
import { noLtpReason } from './useContractLtps';

export interface OpenPositionsPanelProps {
  /** Backend reachability — the header dot, and why a stale list may be stale. */
  backendConnected: boolean;
  /** Open trades as the page maps them from the canonical ledger. */
  backendOpenTrades: any[];
  /** trade id -> { ltp, pnl } from the bridge — cross-check + last-resort price. */
  openPnl: Record<string, { ltp: number; pnl: number }>;
  /** Clicking the header jumps the main pane to the POSITIONS tab. */
  setShowOnlySource: React.Dispatch<React.SetStateAction<'all'|'mamta_ai'|'manual'|'webhook'|'positions'|'traffic'|'promotion'>>;
  /** trade id -> live contract price (useContractLtps). Optional: an older
   *  caller without it falls back to the bridge price. */
  liveLtps?: Record<string, LiveLtp>;
  /** Session realised P&L (the page's todayPnl) and its window label. */
  bookedPnl?: number;
  bookedLabel?: string;
  /** CHART quick action — opens the contract chart drawer for this row. */
  onOpenChart?: (row: any) => void;
}

const sgn = (v: number) => `${v < 0 ? '−' : '+'}₹${fmtM(Math.abs(v))}`;
const px = (v: number | null | undefined) =>
  v == null ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const SRC_COL: Record<string, string> = { ws: '#10b981', chain: '#60a5fa', bridge: '#a78bfa' };
const SRC_TIP: Record<string, string> = {
  ws: 'live tick from the shared /ws/live-feed push (spine)',
  chain: 'from /options/chain (server cache, 3 s) — no WS tick for this strike',
  bridge: 'from /bridge/rag/positions (5 s poll) — no WS tick and not in the chain',
};

export const OpenPositionsPanel: React.FC<OpenPositionsPanelProps> = ({
  backendConnected, backendOpenTrades, openPnl, setShowOnlySource,
  liveLtps, bookedPnl, bookedLabel, onOpenChart,
}) => {
  const ltpOf = (t: any): LiveLtp | null => {
    const lv = liveLtps?.[String(t?.id)];
    if (lv) return lv;
    const b = openPnl[String(t?.id)];
    return b && b.ltp > 0 ? { ltp: b.ltp, at: 0, source: 'bridge' } : null;
  };
  const agg = sumM2M(backendOpenTrades, t => ltpOf(t)?.ltp);
  const booked = Number(bookedPnl);
  const hasBooked = Number.isFinite(booked);
  const m2mCol = agg.m2m == null ? '#64748b' : agg.m2m >= 0 ? '#10b981' : '#f87171';
  const total = hasBooked ? totalPnl(booked, agg.m2m) : null;

  return (
  <>
          {/* OPEN POSITIONS — TOP */}
          <div style={{borderBottom:'1px solid #1e293b',padding:'10px 14px',background:'#070b13'}}>
            <div style={{fontSize:10,fontWeight:800,color:'#94a3b8',textTransform:'uppercase',display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6,gap:8,flexWrap:'wrap'}}>
              <span style={{display:'flex',alignItems:'center',gap:6}}>
                <Activity size={11} color="#10b981"/> OPEN POSITIONS
                {backendConnected && <span style={{fontSize:7,padding:'1px 4px',borderRadius:3,background:'rgba(16,185,129,.15)',color:'#10b981'}}>LIVE</span>}
              </span>
              <span style={{display:'flex',alignItems:'center',gap:8}}>
                <button onClick={()=>setShowOnlySource('positions')} className="lt-btn"
                  title="⚡ POSITIONS tab — EXIT/STOP controls"
                  style={{padding:'2px 8px',background:'rgba(34,211,238,.1)',border:'1px solid rgba(34,211,238,.4)',color:'#22d3ee',fontSize:8,fontWeight:800}}>⚡ MANAGE</button>
                <span style={{color:'#10b981'}}>● {backendOpenTrades.length}</span>
              </span>
            </div>
            {/* P&L TRACKING (God Phase 3 item 11): booked · running M2M · total —
                every figure from pnlMath, none typed here. */}
            <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'baseline',fontSize:8.5,marginBottom:6,fontFamily:'monospace',textTransform:'none'}}>
              <span title={hasBooked ? `realised P&L of closed trades · ${bookedLabel || 'session'} window (netPnl, charges included)` : 'booked P&L not supplied by the page'}>
                <span style={{color:'#475569',fontWeight:800}}>BOOKED </span>
                <span style={{color: hasBooked ? (booked >= 0 ? '#10b981' : '#f87171') : '#64748b', fontWeight:800}}>{hasBooked ? sgn(booked) : '—'}</span>
              </span>
              <span title={agg.open === 0 ? 'no open position'
                : agg.m2m == null ? `${agg.open} open, none has a live price yet` :
                `running (unrealised) M2M = Σ (live − entry) × openQty × dir · ${agg.priced}/${agg.open} priced${agg.priced < agg.open ? ` · ${agg.open - agg.priced} without a live tick` : ''}`}>
                <span style={{color:'#475569',fontWeight:800}}>M2M </span>
                <span style={{color:m2mCol,fontWeight:800}}>{agg.open === 0 ? '—' : agg.m2m == null ? '— (no tick)' : sgn(agg.m2m)}</span>
                {agg.open > 0 && agg.priced < agg.open && <span style={{color:'#fbbf24'}}> {agg.priced}/{agg.open}</span>}
              </span>
              <span title={total == null ? 'needs booked P&L' : `total = booked + running M2M${agg.m2m == null && agg.open ? ' (M2M unknown — total shows booked only)' : ''}`}>
                <span style={{color:'#475569',fontWeight:800}}>TOTAL </span>
                <span style={{color: total == null ? '#64748b' : total >= 0 ? '#10b981' : '#f87171', fontWeight:900}}>{total == null ? '—' : sgn(total)}</span>
              </span>
            </div>
            {backendOpenTrades.length === 0 ? (
              <div style={{fontSize:9,color:'#475569',padding:'4px 0'}}>No open positions — waiting for next signal…</div>
            ) : (
              <div className="lt-scroll" style={{maxHeight:260,overflowY:'auto'}}>
                {backendOpenTrades.slice(0, 14).map((t:any, i:number) => {
                  const p = contractParts(t);
                  const lv = levelsOf(t);
                  const live = ltpOf(t);
                  const m2m = runningM2M(t, live?.ltp);
                  const bridge = openPnl[String(t?.id)];
                  const plCol = m2m == null ? '#64748b' : m2m >= 0 ? '#10b981' : '#f87171';
                  const written = t.action === 'SELL';
                  const qty = Number(t.openQty ?? t.quantity) || 0;
                  const tf = String(t.timeframe || '');
                  return (
                  <div key={t.id || i} style={{padding:'6px 4px',borderBottom:'1px solid #1a1f3a',fontSize:9}}>
                    {/* row 1 — ASSET (underlying · strike CE/PE · expiry) · side · qty · TF · CHART */}
                    <div style={{display:'flex',gap:6,alignItems:'center',minWidth:0}}>
                      <span style={{color:'#e2e8f0',fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1,minWidth:0}}
                            title={`${t.option_symbol || t.symbol}${t.id ? ` · ${t.id}` : ''}${t.botName ? ` · ${t.botName}` : ''}`}>
                        <span>{p.underlying}</span>
                        {p.kind === 'OPTION' && (
                          <>
                            <span style={{color:'#334155'}}> · </span>
                            <span style={{fontFamily:'monospace',color:'#fbbf24'}}>{p.strike || '?'}</span>{' '}
                            <span style={{background:p.optType==='PE'?'#ef4444':'#10b981',color:'#fff',padding:'1px 4px',borderRadius:3,fontSize:8,fontWeight:900,letterSpacing:1}}>{p.optType || 'OPT'}</span>
                          </>
                        )}
                        {p.kind !== 'OPTION' && <span style={{color:'#94a3b8'}}> · {p.kind === 'FUTURE' ? 'FUT' : 'CASH'}</span>}
                        {p.expiryShort && <span style={{color:'#64748b'}}> · {p.expiryShort}</span>}
                      </span>
                      <span style={{color:written?'#f87171':'#10b981',fontWeight:800,fontSize:8}} title={written ? 'SELL-to-open (written)' : 'BUY-to-open'}>{t.action}</span>
                      <span style={{color:'#fbbf24',fontSize:8}}>x{qty}</span>
                      {tf && <span style={{fontFamily:'monospace',fontSize:8,fontWeight:900,color:'#38bdf8',background:'rgba(56,189,248,.12)',padding:'0 4px',borderRadius:3}}>{tf}</span>}
                      {onOpenChart && (
                        <button onClick={()=>onOpenChart(t)} className="lt-btn"
                          title={`Live chart of ${p.label}${tf ? ` at ${tf}` : ''}`}
                          style={{padding:'1px 6px',background:'rgba(99,102,241,.12)',border:'1px solid rgba(99,102,241,.45)',color:'#a5b4fc',fontSize:8,fontWeight:900}}>📈 CHART</button>
                      )}
                    </div>
                    {/* row 2 — averages · live LTP (source) · SL · TARGET, each in its own currency */}
                    <div style={{display:'flex',flexWrap:'wrap',gap:'2px 10px',marginTop:3,fontSize:8,fontFamily:'monospace',color:'#64748b'}}>
                      <span title={written ? 'average SELL price (entry of a written position)' : 'average BUY price (entry)'}>
                        {written ? 'AVG SELL' : 'AVG BUY'} <b style={{color:'#60a5fa'}}>₹{px(lv.entry || null)}</b>
                      </span>
                      <span title={live ? `${SRC_TIP[live.source]}${live.at ? ` · ${Math.max(0, Math.round((Date.now() - live.at) / 1000))}s ago` : ''}` : noLtpReason(p.kind, !!p.strike)}>
                        LTP <b style={{color: live ? '#e2e8f0' : '#475569'}}>{live ? `₹${px(live.ltp)}` : '—'}</b>
                        {live && <span style={{color:SRC_COL[live.source], fontSize:7, fontWeight:800}}> {live.source}</span>}
                      </span>
                      {lv.slPremium != null ? (
                        <span title={`stop-loss in option PREMIUM (dnaSlPremium · ${lv.slPremiumBasis || 'basis not stamped'})${lv.slUnderlying != null ? ` · underlying level ${px(lv.slUnderlying)}` : ''}`}>
                          SL <b style={{color:'#f87171'}}>₹{px(lv.slPremium)}</b><span style={{fontSize:7}}> prem</span>
                        </span>
                      ) : (
                        <span title={lv.slUnderlying != null ? 'stop-loss on the UNDERLYING (dnaSl) — no premium stop stamped on this row' : 'no stop stamped on this row (dnaSl / dnaSlPremium empty)'}>
                          SL <b style={{color: lv.slUnderlying != null ? '#f87171' : '#475569'}}>{lv.slUnderlying != null ? px(lv.slUnderlying) : '—'}</b>{lv.slUnderlying != null && <span style={{fontSize:7}}> und</span>}
                        </span>
                      )}
                      <span title={lv.targetUnderlying != null ? 'profit-booking target on the UNDERLYING (dnaTarget) — the ledger stamps no premium target' : 'no target stamped on this row (dnaTarget empty)'}>
                        TGT <b style={{color: lv.targetUnderlying != null ? '#10b981' : '#475569'}}>{lv.targetUnderlying != null ? px(lv.targetUnderlying) : '—'}</b>{lv.targetUnderlying != null && <span style={{fontSize:7}}> und</span>}
                      </span>
                    </div>
                    {/* row 3 — entry time · running M2M (right) */}
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:3,fontSize:8}}>
                      <span style={{color:'#64748b'}}>⏱ {fmtTime(t.time) || '—'}{t.botName ? <span style={{color:'#475569'}}> · {t.botName}</span> : null}</span>
                      <span style={{color:plCol,fontWeight:800,fontFamily:'monospace'}}
                            title={m2m == null
                              ? (live ? 'open qty or entry price missing on this row' : noLtpReason(p.kind, !!p.strike))
                              : `M2M = (${px(live!.ltp)} − ${px(lv.entry)}) × ${qty}${written ? ' × −1' : ''}` + (bridge ? ` · bridge says ${sgn(bridge.pnl)}` : '')}>
                        M2M {m2m == null ? '—' : sgn(m2m)}
                      </span>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
  </>
  );
};

export default OpenPositionsPanel;
